'use strict'

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames/bind';

import MultiSelect from '@/views/common/MultiSelect';

import s from './styles.scss';

const sx = cn.bind(s);

const sortIcons = {
	'-1': 'sort-down',
	'0': 'sort',
	'1': 'sort-up',
};

const SINGLE_SORT = 'SINGLE_SORT';
const MULTI_SORT = 'MULTI_SORT';
const timeoutIds = {};

let colTimeout;

export default ({
	loadingData,
	columns,
	setColumns,
	jobs,
	sort,
	setSort,
	sortMode,
	distinct,
	filterCtls,
	filters,
	filterOptions,
	setFilters,
	rowItemsChecked,
	checkRow,
	checkAll,
	allChecked,
}) => {

	const [ textBuffs, setTextBuffs ] = useState({
		id: '',
		recipiient: '',
		address: '',
	});

	const [ inputTypeDeadlineFrom, setInputTypeDeadlineFrom ] = useState('text');
	const [ inputTypeDeadlineTo, setInputTypeDeadlineTo ] = useState('text');
	const [ inputTypeDateReceivedFrom, setInputTypeDateReceivedFrom ] = useState('text');
	const [ inputTypeDateReceivedTo, setInputTypeDateReceivedTo ] = useState('text');
	const [ inputTypeStatusDueFrom, setInputTypeStatusDueFrom ] = useState('text');
	const [ inputTypeStatusDueTo, setInputTypeStatusDueTo ] = useState('text');

	const inputTypes = {
		deadline: {
			from: {
				value: inputTypeDeadlineFrom,
				set: setInputTypeDeadlineFrom,
				ref: useRef(null),
			},
			to: {
				value: inputTypeDeadlineTo,
				set: setInputTypeDeadlineTo,
				ref: useRef(null),
			},
		},
		dateReceived: {
			from: {
				value: inputTypeDateReceivedFrom,
				set: setInputTypeDateReceivedFrom,
				ref: useRef(null),
			},
			to: {
				value: inputTypeDateReceivedTo,
				set: setInputTypeDateReceivedTo,
				ref: useRef(null),
			},
		},
		statusDue: {
			from: {
				value: inputTypeStatusDueFrom,
				set: setInputTypeStatusDueFrom,
				ref: useRef(null),
			},
			to: {
				value: inputTypeStatusDueTo,
				set: setInputTypeStatusDueTo,
				ref: useRef(null),
			},
		},
	};

	const onColDblClick = (i, e) => {

		const col = columns[ i ];

		let sortOrder = sort.filter(([ key, val ]) => key !== col.key);

		let newColumns = Object.assign(
			[],
			columns,
			{
				[ i ]: {
					...col,
					sort: 0
				},
			}
		);

		setSort(sortOrder);
		setColumns(newColumns);
	};

	const onColClick = (i, e) => {
			
		const col = columns[ i ];
		const currentSort = col.sort;

		let sortOrder = [ ...sort ];
		let newSort, newColumns;

		if (sortMode === SINGLE_SORT) {

			if (currentSort === 0) {
				newSort = 1;
				sortOrder = [[ col.key, newSort ]];
			} else {
				newSort = (currentSort === -1) ? 1 : -1;
				sortOrder = [[ col.key, newSort ]];
			}

			newColumns = columns.map(s => {
				if (s.key === col.key) {
					return { ...s, sort: newSort };
				}
				return { ...s, sort: 0 };
			});

		} else {

			if (currentSort === 0) {
				newSort = 1;
				sortOrder = [ ...sortOrder, [ col.key, newSort ]];

			} else {

				newSort = (currentSort === -1) ? 1 : -1;

				const orderIndex = sort.findIndex(
					([ fieldName, fieldValue ]) => fieldName === col.key
				);

				sortOrder = Object.assign(
					[],
					sort,
					{ [ orderIndex ]: [ col.key, newSort ] }
				);
			}
			
			newColumns = Object.assign(
				[],
				columns,
				{
					[ i ]: {
						...col,
						sort: newSort,
					},
				}
			);
		}

		setSort(sortOrder);
		setColumns(newColumns);
	};

	const clearFilter = index => () => {

		let value;

		const filter = filters[ index ];

		switch (filter.type) {
			case 'text':
				value = '';
				setTextBuffs({ ...textBuffs, [ filter.key ]: '' });
				break;
			case 'boolean':
				value = undefined;
				break;
			case 'list':
				value = [];
				break;
			case 'date':
				value = [ '', '' ];
				break;
		}

		setFilters(Object.assign(
			[],
			filters,
			{
				[ index ]: {
					...filter,
					value,
				},
			}
		));
	};


	const onDateFromChange = (col, index) => e => {

		const filter = filters[ index ];

		setFilters(Object.assign(
			[],
			filters,
			{
				[ index ]: {
					...filter,
					value: [ e.target.value, filter.value[ 1 ]],
				},
			}
		));
	};

	const onDateToChange = (col, index) => e => {

		const filter = filters[ index ];

		setFilters(Object.assign(
			[],
			filters,
			{
				[ index ]: {
					...filter,
					value: [ filter.value[ 0 ], e.target.value ],
				},
			}
		));
	};

	const onBoolChange = (col, index) => e => {

		const filter = filters[ index ];

		setFilters(Object.assign(
			[],
			filters,
			{
				[ index ]: {
					...filter,
					value: e.target.value,
				},
			}
		));
	};

	const onMultiSelectChange = (col, index) => e => {

		const { target } = e;
		const filter = filters[ index ];
		const value = Array.from(target.selectedOptions, o => o.value);

		setFilters(Object.assign(
			[],
			filters,
			{
				[ index ]: {
					...filter,
					value,
				},
			}
		));
	};

	const onTextValueChange = (col, index) => {

		const filter = filters[ index ];

		return e => {
		
			setTextBuffs({
				...textBuffs,
				[ col.key ]: e.target.value,
			});

			if (timeoutIds[ col.key ]) {
				clearTimeout(timeoutIds[ col.key ]);
			}

			timeoutIds[ col.key ] = setTimeout(
				() => { 
					setFilters(Object.assign(
						[],
						filters,
						{
							[ index ]: {
								...filter,
								value: e.target.value,
							},
						}
					));
				},
				1000
			);
		};
	};

	const tbodyRef = useRef();

	const [ mid, setMid ] = useState();

	useEffect(
		() => {
			var offset = tbodyRef.current.getBoundingClientRect();
			setMid((window.innerHeight - offset.top) / 2 - 20);
		},
		[ filterCtls ]
	);

	return (
		<table className={ s.jobsTable }>
			<thead className={ s.jobsTableHead }>
				<tr>
					<th className={ s.checkCol } onClick={ checkAll }>
						<input
							type='checkbox'
							checked={ allChecked }
							readOnly
						/>
					</th>
				{
					columns.reduce(
						(cols, col, i) => {

							const filter = filters[ i ];

							if (col.visible) {
								return [
									...cols,
									<th
										key={ col.key }
										className={ sx(`${ col.key }Col`) }
									>
										<div
											className={ s.colLabelCont }
											onClick={
												e => {
													if (e.detail > 1) {
														clearTimeout(colTimeout);
														onColDblClick(i, e);

													} else {
														colTimeout = setTimeout(
															() => {
																onColClick(i, e);
															},
															250
														);
													}
												}
											}
										>
											<label className={ s.colLabel }>
												{ col.shortLabel || col.label }
											</label>
											<div className={ s.sortIcon }>
												<FontAwesomeIcon
													icon={
														sortIcons[ col.sort.toString() ]
													}
												/>
											</div>
										</div>
										{ filterCtls && 
											<div className={ s.colFilterCont }>
												{ filter && 
													<div className={ s.formCont }>
													{ filter.type === 'list' && 
														<select
															name={ col.key }
															size={ filterOptions[ filter.key ].length > 5 ? 5 : filterOptions[ filter.key ].length }
															multiple={ filter.multi }
															onChange={ onMultiSelectChange(col, i) }
															value={ filter.value }
														>
															<option hidden disabled value='default'></option>
															{
																filterOptions[ filter.key ].map(
																	(o, i) => (
																		<option key={ i } value={ o.value }>
																			{ o.label }
																		</option>
																	)
																)
															}
														</select>
													}
													{ filter.type === 'text' &&
														<input
															type='text'
															value={ textBuffs[ col.key ] }
															onChange={ onTextValueChange(col, i) }
														/>
													}
													{ filter.type === 'boolean' &&
														<select
															defaultValue='default'
															onChange={ onBoolChange(col, i) }
															value={ filter.value }
														>
															<option hidden disabled value='default'></option>
															<option value={ 0 }>No</option>
															<option value={ 1 }>Yes</option>
														</select>
													}
													{ filter.type === 'date' &&
														<>
															<div className={ s.dateRow }>
																<input
																	type={ inputTypes[ col.key ].from.value }
																	ref={ inputTypes[ col.key ].from.ref }
																	placeholder='From'
																	onChange={ onDateFromChange(col, i)  }
																	value={ filter.value[ 0 ] }
																	onClick={ e => {
																		inputTypes[ col.key ].from.set('date');
																		setTimeout(() => {
																			inputTypes[ col.key ].from.ref.current.showPicker();
																		}, 100);
																	}}
																	onBlur={ e => inputTypes[ col.key ].from.set('text') }
																/>
															</div>
															<div className={ s.dateRow }>
																<input
																	type={ inputTypes[ col.key ].to.value }
																	ref={ inputTypes[ col.key ].to.ref }
																	placeholder='To'
																	onChange={ onDateToChange(col, i)  }
																	value={ filter.value[ 1 ] }
																	onClick={ e => {
																		inputTypes[ col.key ].to.set('date');
																		setTimeout(() => {
																			inputTypes[ col.key ].to.ref.current.showPicker();
																		}, 100);
																	}}
																	onBlur={ e => inputTypes[ col.key ].to.set('text') }
																/>
															</div>
														</>
													}
													{
														(
															(filter.type === 'text' && filter.value) ||
															(filter.type === 'boolean' && filter.value) ||
															(filter.type === 'list' && filter.value.length) ||
															(filter.type === 'date' && (filter.value[ 0 ] || filter.value[ 1 ]))
														) &&
														<div className={ s.clear } onClick={ clearFilter(i) } >
															× Clear
														</div>
													}
													</div>
												}
											</div>
										}
									</th>
								];
							}
							return cols;
						},
						[]
					)
				}
				</tr>
			</thead>
			<tbody ref={ tbodyRef }>
				{ loadingData &&
					<tr className={ sx('loader', 'fade-in') }>
						<td className={ s.loaderInner } style={{ top: mid + 'px' }}>
						</td>
					</tr>
				}
				{
					!!rowItemsChecked.length && jobs.map(
						(job, i) => (
							<tr key={ i } className={ s.jobRow }>
								<td onClick={ () => checkRow(i) }>
									<input
										type='checkbox'
										checked={ rowItemsChecked[ i ].checked }
										readOnly
									/>
								</td>
								{ columns.reduce(
									(cols, col, j) => {
										if (col.visible) {
											return [
												...cols,
												<td key={ j } className={ sx(`${ col.key }Col`) }>
												{ 
													typeof col.getValue === 'function' ?
														col.getValue(job, { distinct }) :
														job[ col.key ]
												}
												</td>
											];
										}
										return cols;
									},
									[]
								)}
							</tr>
						)
					)
				}
			</tbody>
		</table>
	);
};
